/* istanbul ignore file */
import { v4 } from 'uuid';
export const createWorkerPool = (createWorker, poolSize, autoTerminate = false) => {
    poolSize ||= Math.max(2, Math.floor(navigator.hardwareConcurrency / 3));
    const WORKER_TIMEOUT = 30_000;
    const WATCH_INTERVAL = 5_000;
    const workerPool = new Map();
    let intervalId = -1;
    let isWatching = false;
    const watch = () => {
        if (isWatching || !autoTerminate)
            return;
        isWatching = true;
        intervalId = setInterval(() => {
            const now = Date.now();
            workerPool.forEach((workerState, worker, pool) => {
                if (now - workerState.lastHandleTs > WORKER_TIMEOUT) {
                    worker.terminate();
                    pool.delete(worker);
                }
            });
            if (!workerPool.size) {
                isWatching = false;
                clearInterval(intervalId);
            }
        }, WATCH_INTERVAL);
    };
    const updateHandles = (worker, handlesChange) => {
        const state = workerPool.get(worker) || { handles: 0, lastHandleTs: -1 };
        // Update timestamp when handles are added
        const lastHandleTs = handlesChange > 0 ? Date.now() : state.lastHandleTs;
        workerPool.set(worker, {
            handles: state.handles + handlesChange,
            lastHandleTs,
            worker,
        });
    };
    const getWorker = () => {
        watch();
        let worker;
        if (workerPool.size < poolSize) {
            worker = createWorker();
        }
        else {
            // We allocate fairly naively by just picking the first worker w/ the fewest
            // handles. First turn the pool states into a list, then find the state with
            // the fewest handles.
            let fewestHandles = Infinity;
            for (const workerState of workerPool.values()) {
                if (workerState.handles < fewestHandles) {
                    worker = workerState.worker;
                    fewestHandles = workerState.handles;
                }
            }
        }
        updateHandles(worker, 1);
        return worker;
    };
    const exec = async (data) => new Promise((resolve, reject) => {
        const worker = getWorker();
        const messagePayload = { ...data, id: v4() };
        const handleMessage = ({ data }) => {
            // Guard on plain objects for type safety and filter on payload id to match the promise
            // to the message.
            if (data.id !== messagePayload.id)
                return;
            // If the guard is passed, clean up the handlers, resolve the data and
            // remove the worker.
            worker.removeEventListener('error', reject);
            worker.removeEventListener('message', handleMessage);
            resolve(data.payload);
            // Decrement handles. This will remove the worker if autoTerminate is enabled
            updateHandles(worker, -1);
        };
        worker.addEventListener('error', reject);
        worker.addEventListener('message', handleMessage);
        worker.postMessage(messagePayload);
    });
    const dispose = () => {
        workerPool.forEach((_, worker, pool) => {
            worker.terminate();
            pool.delete(worker);
        });
    };
    return { dispose, exec };
};
//# sourceMappingURL=createWorkerPool.js.map